/** 文生图 / 图生图：仅用户明确要求人物时加人像约束；人物照一律半身或全身 */

const NON_HUMAN_RE = /风景|山水|美食|食物|产品|商品|猫|狗|动物|建筑|室内|装修|环境|机房|数据中心|logo|图标|海报文字|landscape|food|product|animal|building|interior|environment|data center|datacenter|server room|icon/i;
const ANGLE_RE = /角度|侧面|侧身|profile|three.?quarter|转身|不同角度|换角度|side view|side-view/i;
const FULL_BODY_RE = /全身|从头到脚|full.?body|head to toe|站姿全身|站立全身|full length/i;
const HALF_BODY_RE = /半身|腰以上|waist.?up|half.?body/i;
const IDENTITY_RE = /同一|同一个人|这个人|这个男生|这个女生|这位|一样的人|同一张脸|保持脸|相同的人|像他|像她|图中的人|照片里的|参考图里的人|身形一样|体型一样|身材一样|同样身材|same person|same face|same guy|same girl|same body|同一人/i;
const WARDROBE_RE = /换装|换衣|穿搭|outfit|wardrobe|不同造型|不同衣服|换衣服/i;
const STYLE_ONLY_RE = /风格|色调|氛围|构图|光影|调色|参考风格|类似这种|like this style|color grading|mood board|style reference|仿照色调/i;
const SCENE_COMPOSITE_RE = /在.{0,20}(风景|场景|画面|背景|环境|实地|这里|图中)|风景里|场景里|背景里|背景中|置身|融入.{0,8}(风景|场景)|放到.{0,12}里|人在|一个人在|一位.{0,6}人|in this (scene|landscape|background|setting)|在这个/i;
const HUMAN_SUBJECT_RE = /人物|人像|人脸|真人|人类|模特|男生|女生|男人|女人|男孩|女孩|亚洲人|中国人|一个人|一位|这位|person|people|human|portrait|model|man|woman|boy|girl|selfie|headshot|waist.?up|half.?body|full.?body/i;

export type ImageGenIntent = 'text' | 'lockface' | 'scene' | 'portrait';

export const DEFAULT_PORTRAIT_ASPECT = '3:4';
export const DEFAULT_SCENE_ASPECT = '16:9';
export const DEFAULT_ASIAN_SUBJECT = '一位中国亚洲人';

const DEFAULT_ASIAN_MANDATE =
  'Default: photorealistic mainland-China East-Asian person (中国亚洲人), natural skin and features, unless user explicitly requests another ethnicity. ' +
  'Each generation is a NEW random person — do NOT reuse or copy faces from reference images or prior outputs. ';

/** 人物照强制半身或全身，禁止大头照 */
const PERSON_FRAMING_MANDATE =
  'MANDATORY person photo framing: HALF-BODY (waist-up, 半身照) OR FULL-BODY (head to feet, 全身照) — NEVER a headshot, face close-up, passport photo, or selfie crop. ' +
  'Camera pulled back at least 2-3 meters. Half-body must show head, neck, both shoulders, arms, chest and torso to waist. ' +
  'Full-body must show the entire person head to feet/legs. Face height only 5-15% of frame, natural proportions. ';

const PERSON_FRAMING_AVOID =
  ' Avoid: close-up, big face, oversized head, headshot, selfie crop, passport photo, face占画面超过一成, extreme zoom, cropped at chin, distorted proportions, uncanny AI look, duplicate faces.';

const PERSON_FRAMING_FINAL =
  ' FINAL CHECK: 人物照必须是半身或全身 — NOT 大头照; face must NOT dominate the frame.';

export function isNonHumanPrompt(text: string): boolean {
  return NON_HUMAN_RE.test(text || '');
}

export function isAngleVariationPrompt(text: string): boolean {
  return ANGLE_RE.test(text || '');
}

export function isFullBodyPrompt(text: string): boolean {
  return FULL_BODY_RE.test(text || '');
}

export function isHalfBodyPrompt(text: string): boolean {
  return HALF_BODY_RE.test(text || '');
}

export function isIdentityPrompt(text: string): boolean {
  return IDENTITY_RE.test(text || '');
}

export function isWardrobePrompt(text: string): boolean {
  return WARDROBE_RE.test(text || '');
}

export function isStyleOnlyPrompt(text: string): boolean {
  return STYLE_ONLY_RE.test(text || '') && !isIdentityPrompt(text);
}

export function isSceneCompositePrompt(text: string): boolean {
  return SCENE_COMPOSITE_RE.test(text || '');
}

export function hasHumanSubjectPrompt(text: string): boolean {
  const u = text || '';
  if (!u.trim() || isNonHumanPrompt(u)) return false;
  return HUMAN_SUBJECT_RE.test(u);
}

export function shouldPreserveIdentity(userPrompt: string, hasReferenceImage: boolean): boolean {
  if (!hasReferenceImage) return false;
  const u = (userPrompt || '').trim();
  return isIdentityPrompt(u) || isWardrobePrompt(u);
}

export function shouldUseSceneComposite(userPrompt: string, hasReferenceImage: boolean): boolean {
  if (!hasReferenceImage) return false;
  const u = (userPrompt || '').trim();
  if (shouldPreserveIdentity(u, true)) return false;
  if (isSceneCompositePrompt(u)) return true;
  if (hasHumanSubjectPrompt(u)) return true;
  return false;
}

export function resolveImageGenIntent(userPrompt: string, hasReferenceImage: boolean): ImageGenIntent {
  if (!hasReferenceImage) return 'text';
  if (shouldPreserveIdentity(userPrompt, true)) return 'lockface';
  if (shouldUseSceneComposite(userPrompt, true)) return 'scene';
  return 'portrait';
}

export function shouldForcePersonFraming(userPrompt: string): boolean {
  const u = (userPrompt || '').trim();
  if (!u) return false;
  return hasHumanSubjectPrompt(u);
}

/** @deprecated use shouldForcePersonFraming */
export function shouldForceHalfBodyPortrait(userPrompt: string): boolean {
  return shouldForcePersonFraming(userPrompt);
}

export function resolvePersonFramingHint(userPrompt: string, preferFullBodyInScene = false): string {
  const u = (userPrompt || '').trim();
  if (isFullBodyPrompt(u)) return ' Use FULL-BODY framing: entire person head to feet. ';
  if (isHalfBodyPrompt(u)) return ' Use HALF-BODY (waist-up) framing. ';
  if (preferFullBodyInScene) return ' Prefer FULL-BODY or half-body in the environment — person naturally scaled in scene, NOT face close-up. ';
  return ' Default HALF-BODY (waist-up) unless full-body fits better. ';
}

export function resolveHalfBodyAspect(userPrompt: string, aspectRatio: string): string {
  if (shouldForcePersonFraming(userPrompt)) return isFullBodyPrompt(userPrompt) ? (aspectRatio || '3:4') : DEFAULT_PORTRAIT_ASPECT;
  return aspectRatio || DEFAULT_PORTRAIT_ASPECT;
}

export function resolveSceneAspect(aspectRatio: string): string {
  return aspectRatio || DEFAULT_SCENE_ASPECT;
}

const ANGLE_HINT =
  ' Vary camera angle (three-quarter / profile) if requested, while keeping half-body or full-body framing — never headshot. ';

const IDENTITY_BODY_LOCK =
  'STRICT body-shape lock: keep EXACT same height, build, body type, shoulder width, torso/waist/hip proportions and overall weight as reference — do NOT make thinner, heavier, taller, shorter or different frame. No sudden weight change (no 忽胖忽瘦). ';

const IDENTITY_LOCKFACE_CORE =
  'STRICT identity lock from reference image: keep the EXACT same person — same face AND same body shape/build, gender, age, skin tone, hairstyle and ethnicity. ' +
  IDENTITY_BODY_LOCK +
  'Only change camera angle, pose, outfit and scene as user requests. ';

const IDENTITY_BODY_AVOID =
  ' Avoid: weight gain, weight loss, slimming, bulking, different body type, taller, shorter, wider/narrower frame, 忽胖忽瘦, 变胖, 变瘦, inconsistent body proportions.';

const IDENTITY_ANGLE_HINT =
  ' Change camera angle and pose as requested while preserving the SAME face AND SAME body shape/build — never alter weight or frame. ';

const SCENE_COMPOSITE_CORE =
  DEFAULT_ASIAN_MANDATE +
  'Reference image is ONLY for scene/background environment — NOT for face identity. ' +
  'Place ONE completely NEW random person into the scene as described. ' +
  'Do NOT copy faces from the reference image. Single person, no duplicate faces in frame. ' +
  'Natural integration into the background, photorealistic. ';

function wrapPersonPrompt(userPrompt: string, core: string, preferFullBodyInScene = false): string {
  const u = (userPrompt || '').trim();
  const framing = resolvePersonFramingHint(u, preferFullBodyInScene);
  const angle = isAngleVariationPrompt(u) ? ANGLE_HINT : '';
  return `${core}${PERSON_FRAMING_MANDATE}${framing}${angle}User request: ${u || DEFAULT_ASIAN_SUBJECT + '半身照,自然光,生活感写实'}。清晰、自然、适合社媒发布${PERSON_FRAMING_AVOID}${PERSON_FRAMING_FINAL}`;
}

export function wrapTextToImagePrompt(userPrompt: string): string {
  const u = userPrompt.trim();
  if (!u || !shouldForcePersonFraming(u)) return u;
  const angle = isAngleVariationPrompt(u) ? ANGLE_HINT : '';
  return `${DEFAULT_ASIAN_MANDATE}${PERSON_FRAMING_MANDATE}${resolvePersonFramingHint(u)}${angle}Subject: ${u}.${PERSON_FRAMING_AVOID}${PERSON_FRAMING_FINAL}`;
}

export function wrapImageToImagePrompt(userPrompt: string): string {
  return wrapTextToImagePrompt(userPrompt || '');
}

export function wrapSceneCompositePrompt(userPrompt: string): string {
  return wrapPersonPrompt(userPrompt, SCENE_COMPOSITE_CORE, true);
}

export function wrapIdentityLockfacePrompt(userPrompt: string): string {
  const u = (userPrompt || '').trim() || '同一人物半身照,自然光,生活感写实';
  const angle = isAngleVariationPrompt(u) ? IDENTITY_ANGLE_HINT : '';
  const wardrobe = isWardrobePrompt(u) ? ' Only change outfit and styling; keep the same face and same body shape. ' : '';
  const framing = resolvePersonFramingHint(u, false);
  return `${IDENTITY_LOCKFACE_CORE}${angle}${wardrobe}${PERSON_FRAMING_MANDATE}${framing}User request: ${u}。清晰、生活感,适合社媒发布${PERSON_FRAMING_AVOID}${IDENTITY_BODY_AVOID}${PERSON_FRAMING_FINAL}`;
}

export function wrapWardrobeLockfacePrompt(outfitDesc: string): string {
  return wrapIdentityLockfacePrompt(outfitDesc || '换装预览');
}

export function wrapWardrobePrompt(outfitDesc: string): string {
  return wrapWardrobeLockfacePrompt(outfitDesc);
}

export function wrapCoverPrompt(platformLabel: string, title: string): string {
  return `${platformLabel}风格封面图,干净、生活感,醒目标题:${title}`;
}

export function resolvePortraitAspect(userPrompt: string, aspectRatio: string): string {
  return resolveHalfBodyAspect(userPrompt, aspectRatio);
}
